import { theme, card, type as text } from "../../theme";

// Reference blocks the standard pages show beside their assessment list: a standard's principles
// (ISO 9001's seven quality principles, ISO 42001's AI principles), the plan-do-check-act map of
// the harmonised clauses, and an ordered list of where to start. Information only.

/** Numbered principle cards above the list. */
export function PrincipleCards({ items, minWidth = 240 }: { items: Array<{ title: string; description: string }>; minWidth?: number }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${minWidth}px, 1fr))`, gap: 8, marginBottom: 20 }}>
      {items.map((p, i) => (
        <div key={p.title} style={{ ...card, padding: "10px 14px", display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span aria-hidden="true" style={{ ...text.meta, width: 20, height: 20, borderRadius: "50%", background: theme.color.accentBg, color: theme.color.accentText, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</span>
          <div>
            <div style={{ ...text.body, fontWeight: 700, color: theme.color.heading, marginBottom: 2 }}>{p.title}</div>
            <div style={text.meta}>{p.description}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Plan-do-check-act across clauses 4 to 10, in the standard's own words. */
export function PdcaReference({ title, accent, phases }: {
  title: string;
  /** The standard's swatch. */
  accent: string;
  phases: Array<{ phase: string; clauses: string; desc: string }>;
}) {
  return (
    <section style={{ ...card, marginTop: 24, padding: 18 }}>
      <h2 style={{ ...text.sectionTitle, fontSize: text.body.fontSize, marginBottom: 12 }}>{title}</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
        {phases.map((p) => (
          <div key={p.phase} style={{ borderLeft: `3px solid ${accent}`, paddingLeft: 12 }}>
            <div style={{ ...text.body, fontWeight: 700, color: theme.color.heading }}>{p.phase}</div>
            <div style={{ ...text.meta, fontWeight: 600, color: theme.color.muted, marginBottom: 3 }}>{p.clauses}</div>
            <div style={text.meta}>{p.desc}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/** An ordered "where to start" list. */
export function OrderedGuide({ title, items }: { title: string; items: Array<{ label: string; reason: string }> }) {
  return (
    <section style={{ ...card, marginTop: 24, padding: 18 }}>
      <h2 style={{ ...text.sectionTitle, fontSize: text.body.fontSize, marginBottom: 12 }}>{title}</h2>
      <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((p, i) => (
          <li key={p.label} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <span aria-hidden="true" style={{ ...text.meta, width: 22, height: 22, borderRadius: "50%", flexShrink: 0, background: theme.color.accentBg, color: theme.color.accentText, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</span>
            <div>
              <div style={{ ...text.body, fontWeight: 700, color: theme.color.heading }}>{p.label}</div>
              <div style={text.meta}>{p.reason}</div>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
